import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { VendorRegistrationService, VendorData } from './vendor-registration.service';


interface DocumentStatus {
  type: string;
  filename: string;
  status: 'approved' | 'pending' | 'rejected';
  submittedDate: string;
}

@Component({
  selector: 'app-vendor-registration',
  templateUrl: './vendor-registration.component.html',
  styleUrls: ['./vendor-registration.component.scss']
})
export class VendorRegistrationComponent implements OnInit {
  currentStep = 1;
  totalSteps = 3;
  isLoading = false;
  decodedToken: any = null;
  
  // Forms
  companyInfoForm: FormGroup;
  documentForm: FormGroup;
  
  // Document upload
  uploadedDocuments: any[] = [];
  documentTypes = [
    { value: 'IRSW-9', label: 'W-9 Form' },
    { value: 'COI', label: 'Certificate of Insurance' },
    { value: 'COA', label: 'Certificate of Authority' },
    { value: 'BusinessLicenseUpload', label: 'Business License' },
    { value: 'VoidedCheck', label: 'Voided Check' }
  ];
  
  // Registration status
  registrationStatus: 'pending' | 'approved' | 'rejected' = 'pending';
  documentStatuses: DocumentStatus[] = [
    { type: 'Voided Check', filename: 'voided_check.pdf', status: 'approved', submittedDate: 'Jan 15, 2025' },
    { type: 'Certificate of Insurance', filename: 'coi.pdf', status: 'pending', submittedDate: 'Jan 15, 2025' },
    { type: 'W-9 Form', filename: 'w9.pdf', status: 'approved', submittedDate: 'Jan 15, 2025' }
  ];
  
  constructor(
    private fb: FormBuilder,
    private vendorService: VendorRegistrationService,
    private route: ActivatedRoute
  ) {
    this.initializeForms();
  }

  ngOnInit(): void {
    // Get the base64Str parameter from the route
    this.route.params.subscribe(params => {
      const base64Str = params['base64Str'];
      if (base64Str) {
        this.decodeToken(base64Str);
      }
    });
  }

  private decodeToken(base64Str: string): void {
    try {
      // Decode the base64 string
      const decodedString = atob(base64Str);
      // Parse the JSON
      this.decodedToken = JSON.parse(decodedString);
      console.log('Decoded token:', this.decodedToken);
      
      // Pre-populate the form with token data if available
      this.populateFormFromToken();
    } catch (error) {
      console.error('Error decoding token:', error);
      alert('Invalid token provided. Please contact support.');
    }
  }

  private populateFormFromToken(): void {
    if (this.decodedToken) {
      // Pre-populate form fields with data from the token
      this.companyInfoForm.patchValue({
        companyName: this.decodedToken.supplierNm || '',
        email: this.decodedToken.email || '',
        primaryContact: this.decodedToken.primaryContact || '',
        phoneNumber: this.decodedToken.mobile || '',
        businessAddress: this.decodedToken.addrLine1 || '',
        taxId: this.decodedToken.federalTaxId || '',
        dbaName: this.decodedToken.dbaNm || '',
        businessType: this.decodedToken.businessType || '',
        federalTaxClassification: this.decodedToken.federalTaxClassification || 'Corporation',
        dunsNo: this.decodedToken.dunsNo || '',
        businessStartDate: this.decodedToken.businessStartDate || '',
        naicsNo: this.decodedToken.naicsNo || '',
        webUrl: this.decodedToken.webUrl || '',
        addrLine1: this.decodedToken.addrLine1 || '',
        addrLine2: this.decodedToken.addrLine2 || '',
        stateTaxId: this.decodedToken.taxCompliance?.stateTaxId || '',
        permitNo: this.decodedToken.taxCompliance?.permitNo || '',
        certifications: this.decodedToken.taxCompliance?.certifications || '',
        bankName: this.decodedToken.bankDetails?.bankName || '',
        accountNo: this.decodedToken.bankDetails?.accountNo || '',
        IFSC: this.decodedToken.bankDetails?.IFSC || '',
        acType: this.decodedToken.bankDetails?.acType || 'Checking',
        paymentMethodPreference: this.decodedToken.bankDetails?.paymentMethodPreference || 'Wire Transfer',
        remiEmail: this.decodedToken.bankDetails?.remiEmail || '',
        insuranceLicense: this.decodedToken.insurance?.license || '',
        insuranceExpDate: this.decodedToken.insurance?.expDate || '',
        erpSupplierId: this.decodedToken.erpSupplierId || '',
        goodsCat: this.decodedToken.goodsCat || '',
        serviceRegions: this.decodedToken.serviceRegions || ''
      });
    }
  }

  private initializeForms(): void {
    this.companyInfoForm = this.fb.group({
      companyName: [''],
      taxId: [''],
      businessAddress: [''],
      primaryContact: [''],
      email: [''],
      phoneNumber: [''],
      businessType: [''],
      dbaName: [''],
      federalTaxClassification: ['Corporation'],
      dunsNo: [''],
      businessStartDate: [''],
      naicsNo: [''],
      webUrl: [''],
      addrLine1: [''],
      addrLine2: [''],
      stateTaxId: [''],
      permitNo: [''],
      certifications: [''],
      bankName: [''],
      accountNo: [''],
      IFSC: [''],
      acType: ['Checking'],
      paymentMethodPreference: ['Wire Transfer'],
      remiEmail: [''],
      insuranceLicense: [''],
      insuranceExpDate: [''],
      erpSupplierId: [''],
      goodsCat: [''],
      serviceRegions: ['']
    });

    this.documentForm = this.fb.group({
      accountType: ['Checking'],
      insuranceExpDate: [''],
      additionalDocuments: ['']
    });
  }

  // Navigation methods
  nextStep(): void {
    if (this.currentStep < this.totalSteps) {
      this.currentStep++;
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  goToStep(step: number): void {
    if (step >= 1 && step <= this.totalSteps) {
      this.currentStep = step;
    }
  }

  // Form submission
  submitCompanyInfo(): void {
    this.nextStep();
  }

  submitDocuments(): void {
    // First register the vendor, then upload documents
    this.registerVendor();
  }

  // File upload methods
  onFileSelected(event: any, documentType: string): void {
    const files = event.target.files;
    if (files && files.length > 0) {
      if (documentType === 'Additional') {
        // Handle multiple files for additional documents
        for (let i = 0; i < files.length; i++) {
          this.uploadDocument(files[i], documentType);
        }
      } else {
        // Handle single file for specific document types
        this.uploadDocument(files[0], documentType);
      }
    }
  }

  onFileDropped(event: any, documentType: string): void {
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      if (documentType === 'Additional') {
        // Handle multiple files for additional documents
        for (let i = 0; i < files.length; i++) {
          this.uploadDocument(files[i], documentType);
        }
      } else {
        // Handle single file for specific document types
        this.uploadDocument(files[0], documentType);
      }
    }
  }

  private uploadDocument(file: File, documentType: string): void {
    // For specific document types, replace existing document
    if (documentType !== 'Additional') {
      this.removeDocumentByType(documentType);
    }
    
    // Add the new document
    this.uploadedDocuments.push({
      type: documentType,
      filename: file.name,
      file: file,
      uploaded: false
    });
    console.log('Document added for upload:', file.name);
  }

  // Document management methods
  getDocumentByType(documentType: string): any {
    return this.uploadedDocuments.find(doc => doc.type === documentType);
  }

  getAdditionalDocuments(): any[] {
    return this.uploadedDocuments.filter(doc => doc.type === 'Additional');
  }

  removeDocument(documentType: string, event: Event): void {
    event.stopPropagation();
    this.removeDocumentByType(documentType);
  }

  removeAdditionalDocument(index: number, event: Event): void {
    event.stopPropagation();
    const additionalDocs = this.getAdditionalDocuments();
    if (additionalDocs[index]) {
      const docToRemove = additionalDocs[index];
      const globalIndex = this.uploadedDocuments.indexOf(docToRemove);
      if (globalIndex > -1) {
        this.uploadedDocuments.splice(globalIndex, 1);
      }
    }
  }

  private removeDocumentByType(documentType: string): void {
    const index = this.uploadedDocuments.findIndex(doc => doc.type === documentType);
    if (index > -1) {
      this.uploadedDocuments.splice(index, 1);
    }
  }

  // Vendor registration
  private registerVendor(): void {
    const vendorData: VendorData = {
      supplierNm: this.companyInfoForm.value.companyName,
      dbaNm: this.companyInfoForm.value.dbaName || this.companyInfoForm.value.companyName,
      businessType: this.companyInfoForm.value.businessType,
      federalTaxClassification: this.companyInfoForm.value.federalTaxClassification,
      federalTaxId: this.companyInfoForm.value.taxId,
      dunsNo: this.companyInfoForm.value.dunsNo,
      businessStartDate: this.companyInfoForm.value.businessStartDate,
      naicsNo: this.companyInfoForm.value.naicsNo,
      webUrl: this.companyInfoForm.value.webUrl,
      primaryContact: this.companyInfoForm.value.primaryContact,
      mobile: this.companyInfoForm.value.phoneNumber,
      email: this.companyInfoForm.value.email,
      addrLine1: this.companyInfoForm.value.addrLine1 || this.companyInfoForm.value.businessAddress,
      addrLine2: this.companyInfoForm.value.addrLine2,
      taxCompliance: {
        stateTaxId: this.companyInfoForm.value.stateTaxId,
        permitNo: this.companyInfoForm.value.permitNo,
        certifications: this.companyInfoForm.value.certifications,
        complianceAgreements: 'Signed'
      },
      bankDetails: {
        bankName: this.companyInfoForm.value.bankName,
        accountNo: this.companyInfoForm.value.accountNo,
        IFSC: this.companyInfoForm.value.IFSC,
        acType: this.companyInfoForm.value.acType,
        paymentMethodPreference: this.companyInfoForm.value.paymentMethodPreference,
        remiEmail: this.companyInfoForm.value.remiEmail
      },
      supportingDoc: [], // Empty initially, will be populated after document uploads
      insurance: {
        license: this.companyInfoForm.value.insuranceLicense,
        certificates: 'InsuranceCert_2025.pdf',
        expDate: this.documentForm.value.insuranceExpDate
      },
      erpSupplierId: this.companyInfoForm.value.erpSupplierId,
      goodsCat: this.companyInfoForm.value.goodsCat,
      serviceRegions: this.companyInfoForm.value.serviceRegions,
      certificationStatus: 'Pending',
      termsAgreement: 'Accepted'
    };

    this.isLoading = true;

    // First register the vendor
    this.vendorService.registerVendor(vendorData).subscribe({
      next: (response: any) => {
        console.log('Vendor registered successfully:', response);
        
        // After successful vendor registration, upload documents if any
        if (this.uploadedDocuments.length > 0) {
          this.uploadAllDocuments();
        } else {
          // No documents to upload, go to status page
          this.isLoading = false;
          this.currentStep = 3;
        }
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error registering vendor:', error);
        alert('Error registering vendor. Please try again.');
      }
    });
  }

  // Upload all documents after vendor registration
  private uploadAllDocuments(): void {
    let uploadCount = 0;
    const totalDocuments = this.uploadedDocuments.length;

    this.uploadedDocuments.forEach((doc, index) => {
      this.vendorService.uploadDocument(doc.file).subscribe({
        next: (response: any) => {
          uploadCount++;
          console.log(`Document ${index + 1} uploaded successfully:`, response);
          
          // Check if all documents are uploaded
          if (uploadCount === totalDocuments) {
            this.isLoading = false;
            this.currentStep = 3; // Go to status page
            console.log('All documents uploaded successfully');
          }
        },
        error: (error) => {
          uploadCount++;
          console.error(`Error uploading document ${index + 1}:`, error);
          
          // Continue with other documents even if one fails
          if (uploadCount === totalDocuments) {
            this.isLoading = false;
            this.currentStep = 3; // Go to status page
            alert('Some documents failed to upload, but vendor registration was successful.');
          }
        }
      });
    });
  }

  // Utility methods

  simulateApproval(): void {
    this.registrationStatus = 'approved';
    this.documentStatuses.forEach(doc => {
      if (doc.status === 'pending') {
        doc.status = 'approved';
      }
    });
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'approved': return 'ri-check-line';
      case 'pending': return 'ri-time-line';
      case 'rejected': return 'ri-close-line';
      default: return 'ri-time-line';
    }
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'approved': return 'text-success';
      case 'pending': return 'text-warning';
      case 'rejected': return 'text-danger';
      default: return 'text-warning';
    }
  }
}
